import * as Yup from 'yup';
import {
  Button,
  FieldGroup,
  Input,
  LinkActor,
  Spacer,
  Title,
} from 'internalLibrary';
import { FC, useMemo } from 'react';
import { LoginFormValues } from '../../../../types/ui';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileIcon from '../../../components/icons/ProfileIcon';
import styles from './Login.module.scss';

const initialValues: LoginFormValues = {
  email: '',
  password: '',
};

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => void;
  errorMessage: string | null;
  isLoading: boolean;
}

export const LoginForm: FC<LoginFormProps> = (props) => {
  const { onSubmit, errorMessage, isLoading } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const passwordRecoveryClicked = () => {
    navigate('/password-recovery');
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().required(t('requiredField')),
        password: Yup.string().required(t('requiredField')),
      }),
    [t],
  );

  const { handleSubmit, handleChange, handleBlur, values, touched, errors } =
    useFormik({
      validationSchema,
      initialValues,
      onSubmit,
    });

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <Title
        icon={<ProfileIcon />}
        text={t('login_title')}
        forceColumn
        className={styles.title}
      />
      <Spacer marginBottom="40px" />
      {errorMessage !== null && (
        <h3 className={styles['error-message']}>{errorMessage}</h3>
      )}
      <FieldGroup validationText={touched.email ? errors.email : ''}>
        <Input
          label={t('emailAddress')}
          name="email"
          touched={touched.email}
          onChange={handleChange}
          error={touched.email && Boolean(errors.email)}
          onBlur={handleBlur}
          value={values.email}
        />
      </FieldGroup>
      <Spacer marginBottom="20px" />
      <FieldGroup validationText={touched.password ? errors.password : ''}>
        <Input
          label={t('login_password')}
          name="password"
          touched={touched.password}
          onChange={handleChange}
          error={touched.password && Boolean(errors.password)}
          onBlur={handleBlur}
          value={values.password}
          type="password"
        />
      </FieldGroup>
      <Spacer marginTop="56px" marginBottom="26px">
        <Button
          className={styles.submit}
          loading={false}
          type="submit"
          disabled={isLoading}
          fill
        >
          {t('login_login')}
        </Button>
      </Spacer>
      <LinkActor
        className={styles['lost-password']}
        linkProps={{
          onClick: passwordRecoveryClicked,
        }}
      >
        {t('login_forgotPassword')}
      </LinkActor>
    </form>
  );
};

export default LoginForm;
